import { useState } from 'react';
import { apiClient } from '../lib/api-client.js';
import { MAX_REPORT_BODY_LENGTH } from '@ridehub/shared';

interface ReportModalProps {
  targetType: 'spot' | 'comment' | 'photo';
  targetId: string;
  onClose: () => void;
}

const REASONS: { value: string; label: string }[] = [
  { value: 'spam', label: 'スパム' },
  { value: 'inappropriate', label: '不適切なコンテンツ' },
  { value: 'wrong_location', label: '場所が間違っている' },
  { value: 'duplicate', label: '重複' },
  { value: 'other', label: 'その他' },
];

export function ReportModal({ targetType, targetId, onClose }: ReportModalProps) {
  const [reason, setReason] = useState('');
  const [body, setBody] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const isOther = reason === 'other';
  const canSubmit = reason !== '' && (!isOther || body.trim().length > 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      await apiClient.post(
        '/reports',
        { targetType, targetId, reason, body: body.trim() || undefined },
        { requireAuth: true }
      );
      setDone(true);
      setTimeout(() => onClose(), 1500);
    } catch (err: unknown) {
      const status = (err as { status?: number })?.status;
      if (status === 409) {
        setError('すでに通報済みです');
      } else {
        setError('通報に失敗しました。もう一度お試しください。');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[3000] flex items-center justify-center bg-black/50 px-4"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-base font-bold text-gray-800">通報する</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 text-xl leading-none"
            aria-label="閉じる"
          >
            ×
          </button>
        </div>

        {done ? (
          <div className="py-6 text-center">
            <p className="text-green-600 font-medium">通報しました</p>
            <p className="text-sm text-gray-400 mt-1">ご報告ありがとうございます</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                通報理由 <span className="text-red-500">*</span>
              </label>
              <select
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="">選択してください</option>
                {REASONS.map(({ value, label }) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                補足コメント{isOther && <span className="text-red-500"> *</span>}
              </label>
              <div className="relative">
                <textarea
                  value={body}
                  onChange={(e) => setBody(e.target.value)}
                  maxLength={MAX_REPORT_BODY_LENGTH}
                  rows={3}
                  placeholder={isOther ? '詳細を入力してください' : '任意'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary"
                />
                <span className="absolute bottom-2 right-2 text-xs text-gray-400">
                  {body.length}/{MAX_REPORT_BODY_LENGTH}
                </span>
              </div>
            </div>

            {error && <p className="text-sm text-red-500">{error}</p>}

            <div className="flex gap-2 pt-1">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                キャンセル
              </button>
              <button
                type="submit"
                disabled={!canSubmit || submitting}
                className="flex-1 px-4 py-2 text-sm text-white bg-red-500 rounded-lg hover:bg-red-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? '送信中...' : '通報する'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
